import { GrupoNovedadesVM, NovedadVM } from "./peritajeViewModel";

/**
 * ── Reparto de "NOVEDADES ENCONTRADAS" en hojas ──────────────────────────────
 *
 * Cada hoja mide lo mismo que un A4, así que la lista de hallazgos no puede crecer sin límite:
 * con 21 novedades la tarjeta se salía por el pie de la hoja 1 y quedaba cortada. Aquí se
 * reparte en dos columnas por hoja, dado el alto libre en la hoja donde empieza y en una hoja
 * nueva; `paginarHojas.ts` decide dónde empieza y qué sigue después.
 *
 * Las filas no envuelven (una línea con elipsis), así que todas miden igual y el reparto se
 * puede calcular con un alto por fila y otro por título, medidos sobre la hoja ya pintada.
 */

export const COLUMNAS_NOVEDADES = 2;

/** Tramo de un grupo que cae en una columna. `continua` si el grupo empezó en la columna anterior. */
export interface BloqueNovedadesVM {
    titulo: string;
    continua: boolean;
    items: NovedadVM[];
}

export type ColumnaNovedadesVM = BloqueNovedadesVM[];

/** Las columnas de la tarjeta de novedades en una hoja. */
export type HojaNovedadesVM = ColumnaNovedadesVM[];

/** En px de la hoja sin escalar. */
export interface MedidasNovedades {
    /** Alto de un título de grupo, con el aire que lo separa del grupo anterior. */
    titulo: number;
    item: number;
    /** Alto libre para la lista en la hoja 1; 0 si ahí ya no cabe ni un grupo. */
    primera: number;
    /** Alto libre para la lista en una hoja de continuación. */
    resto: number;
}

export type AltosNovedades = Pick<MedidasNovedades, "titulo" | "item">;

const altoDe = (bloques: BloqueNovedadesVM[], altos: AltosNovedades): number =>
    bloques.reduce((suma, bloque) => suma + altos.titulo + bloque.items.length * altos.item, 0);

/**
 * Llena columnas en orden sin pasar el alto de cada una. Una columna puede quedar vacía.
 *
 * Con `partir` en false, un grupo que no cabe entero empieza en la columna siguiente, salvo que
 * la actual esté vacía: ahí no hay más remedio que partirlo.
 */
const llenar = (
    bloques: BloqueNovedadesVM[],
    altos: AltosNovedades,
    capacidad: (columna: number) => number,
    partir = true,
): ColumnaNovedadesVM[] => {
    const minimo = altos.titulo + altos.item;
    const columnas: ColumnaNovedadesVM[] = [];
    let actual: ColumnaNovedadesVM = [];
    let libre = capacidad(0);

    const siguiente = () => {
        columnas.push(actual);
        actual = [];
        libre = capacidad(columnas.length);
    };

    bloques.forEach((bloque) => {
        let pendientes = bloque.items;
        let continua = bloque.continua;
        while (pendientes.length > 0) {
            const entero = altos.titulo + pendientes.length * altos.item;
            // Un título nunca queda solo al pie de una columna: necesita al menos una fila debajo.
            // Una columna vacía solo se salta si no tiene alto (la hoja 1 sin espacio); si no,
            // recibe al menos una fila, o el reparto no terminaría nunca.
            const saltar = actual.length === 0 ? libre <= 0 : libre < minimo || (!partir && libre < entero);
            if (saltar) {
                siguiente();
                continue;
            }
            const caben = Math.max(1, Math.floor((libre - altos.titulo) / altos.item));
            const items = pendientes.slice(0, caben);
            actual.push({ titulo: bloque.titulo, continua, items });
            libre -= altos.titulo + items.length * altos.item;
            pendientes = pendientes.slice(caben);
            continua = true;
        }
    });

    if (actual.length > 0) columnas.push(actual);
    return columnas;
};

/** Vuelve a juntar un grupo que quedó partido entre las dos columnas de una misma hoja. */
const unirTramos = (bloques: BloqueNovedadesVM[]): BloqueNovedadesVM[] =>
    bloques.reduce<BloqueNovedadesVM[]>((unidos, bloque) => {
        const previo = unidos[unidos.length - 1];
        if (previo && bloque.continua && previo.titulo === bloque.titulo) {
            unidos[unidos.length - 1] = { ...previo, items: [...previo.items, ...bloque.items] };
        } else {
            unidos.push(bloque);
        }
        return unidos;
    }, []);

/** Partir un grupo entre columnas solo compensa si deja las columnas más de dos filas más bajas. */
const FILAS_TOLERADAS = 2;

/**
 * Reparto en las dos columnas con el alto más bajo posible, o `null` si no caben. Sin `partir`,
 * solo vale un reparto en el que ningún grupo quedó en dos tramos.
 */
const repartirEnDos = (
    bloques: BloqueNovedadesVM[],
    altos: AltosNovedades,
    capacidad: number,
    partir: boolean,
): HojaNovedadesVM | null => {
    const desde = Math.max(Math.ceil(altoDe(bloques, altos) / COLUMNAS_NOVEDADES), altos.titulo + altos.item);
    for (let alto = desde; alto <= capacidad; alto += 1) {
        const columnas = llenar(bloques, altos, () => alto, partir);
        const enteros = columnas.flat().length === bloques.length;
        if (columnas.length <= COLUMNAS_NOVEDADES && (partir || enteros)) return columnas;
    }
    return null;
};

/** Alto de la lista en una hoja: el de su columna más alta. */
export const altoNovedades = (hoja: HojaNovedadesVM, altos: AltosNovedades): number =>
    Math.max(0, ...hoja.map((columna) => altoDe(columna, altos)));

/**
 * La última hoja casi nunca va llena: en vez de dejar la columna izquierda completa y la derecha
 * con dos filas, reparte todo entre las dos columnas. Si partir un grupo casi no ahorra alto, se
 * prefiere dejarlo entero, que se lee mejor que un "(cont.)" por una sola fila.
 */
const equilibrar = (hoja: HojaNovedadesVM, altos: AltosNovedades, capacidad: number): HojaNovedadesVM => {
    const bloques = unirTramos(hoja.flat());
    if (bloques.length === 0) return hoja;

    const partidas = repartirEnDos(bloques, altos, capacidad, true);
    if (!partidas) return hoja;
    const enteras = repartirEnDos(bloques, altos, capacidad, false);
    if (enteras && altoNovedades(enteras, altos) - altoNovedades(partidas, altos) <= FILAS_TOLERADAS * altos.item) {
        return enteras;
    }
    return partidas;
};

/**
 * Reparte los grupos de novedades en hojas de dos columnas. La primera hoja devuelta es la parte
 * que va en la hoja 1 del certificado (puede venir vacía si ahí no queda espacio); las demás
 * son hojas de continuación.
 */
export const paginarNovedades = (grupos: GrupoNovedadesVM[], medidas: MedidasNovedades): HojaNovedadesVM[] => {
    const minimo = medidas.titulo + medidas.item;
    const primera = medidas.primera >= minimo ? medidas.primera : 0;
    // Piso de seguridad: si una medición llegara mal, una hoja de continuación sin espacio para
    // un solo grupo dejaría el reparto en un ciclo sin fin.
    const resto = Math.max(medidas.resto, minimo);
    const capacidadDe = (columna: number) => (columna < COLUMNAS_NOVEDADES ? primera : resto);

    const bloques = grupos.map((grupo) => ({ titulo: grupo.titulo, continua: false, items: grupo.items }));
    const columnas = llenar(bloques, medidas, capacidadDe);
    if (columnas.length === 0) return [];

    const hojas: HojaNovedadesVM[] = [];
    for (let i = 0; i < columnas.length; i += COLUMNAS_NOVEDADES) {
        hojas.push(columnas.slice(i, i + COLUMNAS_NOVEDADES));
    }

    const ultima = hojas.length - 1;
    hojas[ultima] = equilibrar(hojas[ultima], medidas, ultima === 0 ? primera : resto);
    return hojas;
};
